/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import request from './index';
import { BACKEND_ERROR_MESSAGE, PL_CREATION_PRIMARY_PRODUCT } from '../utils/constants/content-production';
import { getApplicationFromSapCode } from '../utils/constants';
import IMS from './ims';
import {
    getActionMapping,
    RESOURCE_TYPE_TO_REQUEST_KEY,
    RESOURCE_TYPE_TO_RESPONSE_KEY,
    DOMAIN_LCP,
    type ActionMapping,
    type ResourceType,
    type Domain,
} from './permissions/actionMapper';

const BASE_LCS_API_URL = process.env.LCS_API_ENDPOINT ? process.env.LCS_API_ENDPOINT : 'http://localhost:8080/api/';
const API_KEY = 'LearnContentPortal1';

interface AuthorizationRequest {
    action: string;
    resource: Record<string, unknown>;
}

interface AuthorizationResult {
    action: string;
    resource: Record<string, unknown>;
    decision: 'ALLOW' | 'DENY';
    reasons?: string[];
    request?: Record<string, unknown>;
    determiningPolicies?: Record<string, unknown>[];
    errors?: Record<string, unknown>[];
}

interface AuthorizationResponse {
    tutorialResults?: AuthorizationResult[];
    playlistResults?: AuthorizationResult[];
    collectionResults?: AuthorizationResult[];
    queryResults?: AuthorizationResult[];
    uploadResults?: AuthorizationResult[];
    tagResults?: AuthorizationResult[];
    userRecordResults?: AuthorizationResult[];
    userId?: string;
}

export interface CheckMultipleCapabilitiesResult {
    permissions: Record<string, boolean>;
    /** SAP codes from collection placeholders that returned ALLOW (for collection create actions) */
    collectionCreateAllowedSapCodes?: string[];
    /** SAP codes from playlist placeholders that returned ALLOW (for playlist create actions) */
    playlistCreateAllowedSapCodes?: string[];
}

class AuthorizationService {
    // Cache for authorization results to prevent duplicate API calls
    private static cache = new Map<string, { result: boolean; timestamp: number }>();
    private static CACHE_TTL = 60000; // 1 minute cache

    private _getRequestHeaders = () => ({
        'x-api-key': API_KEY,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: '',
    });

    private _getBaseHeaders = () => {
        const token = IMS.getInstance().getAccessToken();
        if (!token) {
            throw new Error('Unable to get access token from IMS. User may not be authenticated.');
        }
        return {
            ...this._getRequestHeaders(),
            authorization: `Bearer ${token}`,
        };
    };

    /**
     * Get cached result or return null if expired/not found
     */
    private static _getCached(key: string): boolean | null {
        const cached = AuthorizationService.cache.get(key);
        if (!cached) return null;

        const now = Date.now();
        if (now - cached.timestamp > AuthorizationService.CACHE_TTL) {
            AuthorizationService.cache.delete(key);
            return null;
        }

        return cached.result;
    }

    /**
     * Cache authorization result
     */
    private static _setCached(key: string, result: boolean): void {
        AuthorizationService.cache.set(key, {
            result,
            timestamp: Date.now(),
        });
    }

    /**
     * Get request body key for a resource type
     */
    private _getRequestKey(resourceType: ResourceType): string {
        return RESOURCE_TYPE_TO_REQUEST_KEY[resourceType];
    }

    /**
     * Get response key for a resource type
     */
    private _getResponseKey(resourceType: ResourceType): keyof AuthorizationResponse {
        return RESOURCE_TYPE_TO_RESPONSE_KEY[resourceType] as keyof AuthorizationResponse;
    }

    /**
     * Build action mappings for Cartesian product: each action applied to each resource.
     * Order: action[0]×all resources, then action[1]×all resources, ...
     * So result index (i * resources.length + j) = (action i, resource j).
     * @param domain Domain constant ('lcp' or 'tbw')
     * @param frontendActions Array of frontend action names
     * @param resources Array of resources
     * @returns Array of action mappings with resources (length = frontendActions.length * resources.length)
     */
    private _buildActionMappingsForCartesianProduct(
        domain: Domain,
        frontendActions: string[],
        resources: Array<Record<string, unknown>>,
    ): Array<{ action: string; mapping: ActionMapping; resource?: Record<string, unknown> }> {
        const result: Array<{ action: string; mapping: ActionMapping; resource?: Record<string, unknown> }> = [];
        for (let i = 0; i < frontendActions.length; i++) {
            const action = frontendActions[i];
            const mapping = getActionMapping(action, domain);
            if (!mapping) {
                throw new Error(`No mapping found for action: ${action} in domain: ${domain}`);
            }
            if (mapping.domain !== domain) {
                throw new Error(
                    `Action ${action} domain (${mapping.domain}) does not match requested domain (${domain})`,
                );
            }
            for (let j = 0; j < resources.length; j++) {
                result.push({ action, mapping, resource: resources[j] });
            }
        }
        return result;
    }

    /**
     * Build action mappings with placeholder resources per action (used when no resources array is provided).
     * For each action, builds placeholder resource(s) (e.g. for create/capability checks).
     * @param domain Domain constant ('lcp' or 'tbw')
     * @param frontendActions Array of frontend action names
     * @param locale Optional locale for placeholder resources (e.g. from dashboard locale selector)
     * @returns Array of action mappings with resources
     */
    private _buildActionMappingsForPlaceholderResources(
        domain: Domain,
        frontendActions: string[],
        locale?: string,
    ): Array<{ action: string; mapping: ActionMapping; resource?: Record<string, unknown> }> {
        const actionMappings: Array<{ action: string; mapping: ActionMapping; resource?: Record<string, unknown> }> =
            [];

        frontendActions.forEach((action) => {
            const mapping = getActionMapping(action, domain);
            if (!mapping) {
                throw new Error(`No mapping found for action: ${action} in domain: ${domain}`);
            }
            if (mapping.domain !== domain) {
                throw new Error(
                    `Action ${action} domain (${mapping.domain}) does not match requested domain (${domain})`,
                );
            }

            let finalResources: Array<Record<string, unknown>>;
            if (!mapping.requiresResource) {
                finalResources = [{}];
            } else {
                const resourceType = mapping.resourceType;
                const status = this._extractStatusFromAction(action);
                if (resourceType === 'collection') {
                    finalResources = this._buildPlaceholderResourcesForAllPrimaryProducts('collection', status, locale);
                } else if (resourceType === 'playlist') {
                    finalResources = this._buildPlaceholderResourcesForAllPrimaryProducts('playlist', status, locale);
                } else if (resourceType === 'tutorial') {
                    finalResources = this._buildPlaceholderResourcesForAllPrimaryProducts('tutorial', status, locale);
                } else {
                    finalResources = [{}];
                }
            }

            finalResources.forEach((finalResource) => {
                actionMappings.push({ action, mapping, resource: finalResource });
            });
        });

        return actionMappings;
    }

    /**
     * Unified request builder: actions × resources (Cartesian when resources provided, else placeholder per action).
     * @param domain Domain constant ('lcp' or 'tbw')
     * @param frontendActions Single frontend action name or array (normalized to array)
     * @param resources Optional array of resources; when provided, each action is applied to each resource
     * @param options Optional options (e.g. locale for placeholder resources when no resources provided)
     * @returns Request body ready to be sent to makeBatchAuthorizationRequest
     */
    private _buildRequestBody(
        domain: Domain,
        frontendActions: string | string[],
        resources?: Array<Record<string, unknown>>,
        options?: { locale?: string },
    ): Record<string, AuthorizationRequest[]> {
        const actions = typeof frontendActions === 'string' ? [frontendActions] : frontendActions;
        const hasResources = resources && resources.length > 0;

        const actionMappings = hasResources
            ? this._buildActionMappingsForCartesianProduct(domain, actions, resources)
            : this._buildActionMappingsForPlaceholderResources(domain, actions, options?.locale);

        if (actionMappings.length === 0) {
            throw new Error('No valid action mappings found');
        }

        // Group by resource type
        const groups = new Map<
            ResourceType,
            Array<{ action: string; mapping: ActionMapping; resource?: Record<string, unknown> }>
        >();
        actionMappings.forEach(({ action, mapping, resource }) => {
            const resourceType = mapping.resourceType;
            if (!groups.has(resourceType)) {
                groups.set(resourceType, []);
            }
            groups.get(resourceType)!.push({ action, mapping, resource });
        });

        // Build request body grouped by resource type
        const requestBody: Record<string, AuthorizationRequest[]> = {};
        groups.forEach((items, resourceType) => {
            const requestKey = this._getRequestKey(resourceType);
            requestBody[requestKey] = items.map(({ mapping, resource }) => {
                const finalResource = resource ?? {};

                return {
                    action: mapping.backendAction,
                    resource: finalResource,
                };
            });
        });

        return requestBody;
    }

    /**
     * Build request body and call authorization service
     * @param domain Domain constant ('lcp' or 'tbw')
     * @param frontendActions Single frontend action name or array of frontend action names
     * @param resources Optional array of resources. Single action × each resource; array of actions × each resource (Cartesian) or no resources (e.g. capability checks)
     * @param options Optional options (e.g. locale for placeholder resources when no resources provided)
     * @returns Authorization response or null on error
     */
    private async authorize(
        domain: Domain,
        frontendActions: string | string[],
        resources?: Array<Record<string, unknown>>,
        options?: { locale?: string },
    ): Promise<AuthorizationResponse | null> {
        if (Array.isArray(frontendActions) && frontendActions.length === 0) {
            return null;
        }
        const requestBody = this._buildRequestBody(domain, frontendActions, resources, options);
        return this._makeBatchAuthorizationRequest(domain, requestBody);
    }

    /**
     * LCP arm of User Management visibility (LIST_USER_RECORDS in the lcp domain).
     * Caller (appSlice) OR's this with the TBW arm in `TbwAuthorizationService.canViewUserManagementInTBW`.
     */
    async canViewUserManagementInLCP(): Promise<boolean> {
        const frontendAction = 'view-user-management';
        // LIST_USER_RECORDS requires entityType: 'user' (or 'user_request') in the resource object.
        const resources = [{ entityType: 'user' }];
        const response = await this.authorize(DOMAIN_LCP, frontendAction, resources);
        return response?.userRecordResults?.[0]?.decision === 'ALLOW';
    }

    /**
     * Check if user can perform search actions (SEARCH_TUTORIALS, SEARCH_PLAYLISTS, or SEARCH_COLLECTIONS)
     * Uses frontend actions to get backend actions and resource types from action mapper
     * @returns Object with individual permissions for each search action
     */
    async getSearchPermissions(): Promise<{
        canSearchTutorials: boolean;
        canSearchPlaylists: boolean;
        canSearchCollections: boolean;
        hasAnyPermission: boolean;
    }> {
        console.log('getSearchPermissions: Starting batch permission check...');

        const frontendActions = ['search-tutorials', 'search-playlists', 'search-collections'];
        const response = await this.authorize(DOMAIN_LCP, frontendActions);

        if (!response || !response.queryResults || response.queryResults.length === 0) {
            console.log('getSearchPermissions: No valid response, denying all access');
            return {
                canSearchTutorials: false,
                canSearchPlaylists: false,
                canSearchCollections: false,
                hasAnyPermission: false,
            };
        }

        const canSearchTutorials = response.queryResults[0]?.decision === 'ALLOW';
        const canSearchPlaylists = response.queryResults[1]?.decision === 'ALLOW';
        const canSearchCollections = response.queryResults[2]?.decision === 'ALLOW';
        const hasAnyPermission = canSearchTutorials || canSearchPlaylists || canSearchCollections;

        console.log('getSearchPermissions: Results:', {
            canSearchTutorials,
            canSearchPlaylists,
            canSearchCollections,
            hasAnyPermission,
        });

        return {
            canSearchTutorials,
            canSearchPlaylists,
            canSearchCollections,
            hasAnyPermission,
        };
    }

    /**
     * Shared logic for batch resource permission checks: filter to valid resources (e.g. with metadata/applications),
     * call authorize with only those, then map results back to full length (invalid => false).
     */
    private async _batchCheckResourcePermissions<T>(params: {
        frontendActions: string[];
        resources: T[];
        isValid: (r: T) => boolean;
        getPayload: (validResources: T[]) => Array<Record<string, unknown>>;
        domain: Domain;
        getResults: (response: AuthorizationResponse | null) => AuthorizationResult[] | undefined;
        logLabel: string;
    }): Promise<boolean[]> {
        const { frontendActions, resources, isValid, getPayload, domain, getResults, logLabel } = params;
        const totalPairs = frontendActions.length * resources.length;
        console.log(
            `Checking ${logLabel} permissions for ${frontendActions.length} action(s) × ${resources.length} ${logLabel} = ${totalPairs}...`,
        );

        const validIndices = resources.map((r, i) => (isValid(r) ? i : -1)).filter((i) => i >= 0);
        const validResources = validIndices.map((i) => resources[i]);

        if (validResources.length === 0) {
            return Array(totalPairs).fill(false);
        }

        const payload = getPayload(validResources);
        const response = await this.authorize(domain, frontendActions, payload);
        const apiResults = getResults(response);

        if (!response || !apiResults) {
            console.log(`No ${logLabel} results from authorization API`);
            return Array(totalPairs).fill(false);
        }

        const fullResults: boolean[] = [];
        for (let i = 0; i < totalPairs; i++) {
            const actionIdx = Math.floor(i / resources.length);
            const resourceIdx = i % resources.length;
            if (!isValid(resources[resourceIdx])) {
                fullResults.push(false);
            } else {
                const k = validIndices.indexOf(resourceIdx);
                const resultIdx = actionIdx * validResources.length + k;
                fullResults.push(apiResults[resultIdx].decision === 'ALLOW');
            }
        }
        return fullResults;
    }

    /**
     * Generic method to check permissions for resources (playlists or collections)
     * Each action is applied to each resource (Cartesian product); one batch request.
     * @param resourceType The type of resource ('playlist' or 'collection')
     * @param frontendActions Array of frontend action names (e.g. 4 actions)
     * @param resources Array of resource objects (e.g. 100 resources) → 4×100 = 400 request items
     * @returns Array of boolean results, row-major: results[i * resources.length + j] = frontendActions[i] on resources[j]
     */
    private async checkResourcePermissions(
        resourceType: 'playlist' | 'collection',
        frontendActions: string[],
        resources: Array<{
            aem_id: string;
            status: string;
            locale: string;
            applications?: { sap_code: string[]; fi_code: string[] };
        }>,
    ): Promise<boolean[]> {
        const responseKey = RESOURCE_TYPE_TO_RESPONSE_KEY[resourceType] as keyof AuthorizationResponse;
        return this._batchCheckResourcePermissions({
            frontendActions,
            resources,
            // Skip resources with missing or empty sap_code/fi_code. The authorize API returns
            // 400 if either array is empty / contains only empty strings (e.g. orphan items
            // like "collection/undefined/123"). Invalid resources resolve to false locally.
            isValid: (r) => {
                const apps = r.applications;
                if (!apps) return false;
                const hasSap = Array.isArray(apps.sap_code) && apps.sap_code.some((v) => v !== '');
                const hasFi = Array.isArray(apps.fi_code) && apps.fi_code.some((v) => v !== '');
                return hasSap && hasFi;
            },
            getPayload: (valid) => valid,
            domain: DOMAIN_LCP,
            getResults: (res) => res?.[responseKey] as AuthorizationResult[] | undefined,
            logLabel: resourceType,
        });
    }

    /**
     * Check permissions for playlists
     * Each action is applied to each playlist (Cartesian product). Result order: results[i * playlists.length + j] = frontendActions[i] on playlists[j]
     * @param frontendActions Array of frontend action names
     * @param playlists Array of playlist objects with metadata
     * @returns Array of boolean results (length = frontendActions.length * playlists.length)
     */
    async checkPlaylistPermissions(
        frontendActions: string[],
        playlists: Array<{
            aem_id: string;
            status: string;
            locale: string;
            applications?: { sap_code: string[]; fi_code: string[] };
        }>,
    ): Promise<boolean[]> {
        return this.checkResourcePermissions('playlist', frontendActions, playlists);
    }

    /**
     * Check permissions for collections
     * Each action is applied to each collection (Cartesian product). Result order: results[i * collections.length + j] = frontendActions[i] on collections[j]
     * @param frontendActions Array of frontend action names
     * @param collections Array of collection objects with metadata
     * @returns Array of boolean results (length = frontendActions.length * collections.length)
     */
    async checkCollectionPermissions(
        frontendActions: string[],
        collections: Array<{
            aem_id: string;
            status: string;
            locale: string;
            applications?: { sap_code: string[]; fi_code: string[] };
        }>,
    ): Promise<boolean[]> {
        return this.checkResourcePermissions('collection', frontendActions, collections);
    }

    /**
     * Check permissions for tutorials.
     * Each action is applied to each tutorial (Cartesian product). Result order: results[i * tutorials.length + j] = frontendActions[i] on tutorials[j]
     * @param frontendActions Array of frontend action names
     * @param tutorials Array of tutorial objects with metadata
     * @returns Array of boolean results (length = frontendActions.length * tutorials.length)
     */
    async checkTutorialPermissions(
        frontendActions: string[],
        tutorials: Array<{
            aem_id: string;
            status: string;
            locale: string;
            metadata?: {
                application?: {
                    sap_code: string;
                    fi_code: string;
                };
            };
            platforms?: string[];
        }>,
    ): Promise<boolean[]> {
        if (frontendActions.length === 0) {
            return [];
        }
        const mapping = getActionMapping(frontendActions[0]);
        const domain = mapping?.domain ?? DOMAIN_LCP;
        return this._batchCheckResourcePermissions({
            frontendActions,
            resources: tutorials,
            isValid: (t) => !!t.metadata,
            getPayload: (valid) =>
                valid.map((t) => ({
                    aem_id: t.aem_id,
                    status: t.status,
                    locale: t.locale,
                    metadata: {
                        ...t.metadata!,
                        ...(t.platforms != null && { platforms: t.platforms }),
                    },
                })),
            domain,
            getResults: (res) => res?.tutorialResults,
            logLabel: 'tutorial',
        });
    }

    /**
     * Check multiple capabilities at once (uses batch API)
     * @param domain Domain constant ('lcp' or 'tbw')
     * @param frontendActions Array of frontend action names
     * @param options Optional options (e.g. locale for placeholder resources, from dashboard locale selector)
     * @returns Permissions map and, when collection create actions are included, sap_codes from ALLOW collection placeholders
     */
    async checkMultipleCapabilities(
        domain: Domain,
        frontendActions: string[],
        options?: { locale?: string },
    ): Promise<CheckMultipleCapabilitiesResult> {
        const emptyPermissions = frontendActions.reduce(
            (acc, action) => {
                acc[action] = false;
                return acc;
            },
            {} as Record<string, boolean>,
        );

        if (frontendActions.length === 0) {
            return { permissions: {} };
        }

        // Get mappings for all actions to extract results later
        const actionMappings = frontendActions
            .map((action) => {
                const mapping = getActionMapping(action, domain);
                if (!mapping) {
                    return null;
                }
                return { action, mapping };
            })
            .filter((item): item is { action: string; mapping: ActionMapping } => item !== null);

        if (actionMappings.length === 0) {
            return { permissions: emptyPermissions };
        }

        // Build request body to get the actual action mappings (including expanded placeholders)
        const requestBody = this._buildRequestBody(domain, frontendActions, undefined, options);

        // Extract all action mappings from the request body to track expanded placeholders
        const expandedActionMappings: Array<{ action: string; mapping: ActionMapping }> = [];
        Object.entries(requestBody).forEach(([_requestKey, requests]) => {
            requests.forEach((req) => {
                actionMappings.forEach(({ action, mapping }) => {
                    if (mapping.backendAction === req.action) {
                        expandedActionMappings.push({ action, mapping });
                    }
                });
            });
        });

        const response = await this.authorize(domain, frontendActions, undefined, options);

        const results: Record<string, boolean> = {};
        if (!response) {
            actionMappings.forEach(({ action }) => {
                results[action] = false;
            });
        } else {
            const domainResults = this._extractBatchResults(response, expandedActionMappings);
            Object.assign(results, domainResults);
        }

        frontendActions.forEach((action) => {
            if (!(action in results)) {
                results[action] = false;
            }
        });

        const collectionCreateAllowedSapCodes = this._extractAllowedSapCodesFromResults(response?.collectionResults);
        const playlistCreateAllowedSapCodes = this._extractAllowedSapCodesFromResults(response?.playlistResults);

        return {
            permissions: results,
            ...(collectionCreateAllowedSapCodes && { collectionCreateAllowedSapCodes }),
            ...(playlistCreateAllowedSapCodes && { playlistCreateAllowedSapCodes }),
        };
    }

    /**
     * Extract sap_codes from authorization results where decision === 'ALLOW' and resource has applications.
     * Used for both collection and playlist create allowed SAP codes.
     */
    private _extractAllowedSapCodesFromResults(results: AuthorizationResult[] | undefined): string[] | undefined {
        if (!results?.length) return undefined;
        const allowed = new Set<string>();
        for (const r of results) {
            if (r.decision === 'ALLOW' && r.resource?.applications) {
                const apps = r.resource.applications as { sap_code?: string[] };
                const code = apps.sap_code?.[0];
                if (code) allowed.add(code);
            }
        }
        return allowed.size > 0 ? [...allowed] : undefined;
    }

    /**
     * Get authorization API URL for a domain. Only LCP is handled here; TBW lives in
     * `TbwAuthorizationService` and hits `${INAPP}/v1/domains/tbw/authorization`.
     */
    private _getAuthorizationUrl(domain: Domain): string {
        if (domain === DOMAIN_LCP) {
            return `${BASE_LCS_API_URL}/v1/domains/lcp/authorization`;
        }
        throw new Error(`Unknown domain: ${String(domain)}`);
    }

    /**
     * Make a single batch authorization HTTP request
     * This is the only method that makes HTTP requests to the authorization service
     * All other methods build request bodies and call this method
     * @param domain Domain ('lcp' or 'tbw')
     * @param requestBody Request body with appropriate request arrays (playlistRequests, collectionRequests, queryRequests, etc.)
     * @returns Authorization response or null on error
     */
    private async _makeBatchAuthorizationRequest(
        domain: Domain,
        requestBody: Record<string, AuthorizationRequest[]>,
    ): Promise<AuthorizationResponse | null> {
        try {
            const headers = this._getBaseHeaders();
            const url = this._getAuthorizationUrl(domain);
            const responseData = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', requestBody);

            if (responseData?.failed) {
                console.error(`${domain} batch authorization failed:`, responseData);
                return null;
            }

            return responseData?.data as AuthorizationResponse;
        } catch (error) {
            console.error(`${BACKEND_ERROR_MESSAGE} ${domain} > batch authorization > ${error}`);
            return null;
        }
    }

    /**
     * Extract results for a specific resource type from the response
     * When multiple placeholders exist for the same action (e.g., collection creation for all products),
     * the action is ALLOW if ANY of the placeholders return ALLOW
     */
    private _extractResultsForResourceType(
        response: AuthorizationResponse,
        items: Array<{ action: string; mapping: ActionMapping }>,
        resourceType: ResourceType,
    ): Record<string, boolean> {
        const results: Record<string, boolean> = {};
        const filteredItems = items.filter((item) => item.mapping.resourceType === resourceType);

        if (filteredItems.length === 0) {
            return results;
        }

        const responseKey = this._getResponseKey(resourceType);
        const responseResults = response[responseKey] as AuthorizationResult[] | undefined;

        if (responseResults && responseResults.length > 0) {
            // Group items by action to handle multiple placeholders per action
            const actionGroups = new Map<string, number[]>();
            filteredItems.forEach(({ action }, index) => {
                if (!actionGroups.has(action)) {
                    actionGroups.set(action, []);
                }
                actionGroups.get(action)!.push(index);
            });

            // For each action, check if ANY of its results are ALLOW
            actionGroups.forEach((indices, action) => {
                const hasAllow = indices.some((index) => responseResults[index]?.decision === 'ALLOW');
                results[action] = hasAllow;
            });
        }

        return results;
    }

    /**
     * Extract permission results from authorization response
     * Maps response arrays back to frontend action names
     */
    private _extractBatchResults(
        response: AuthorizationResponse,
        items: Array<{ action: string; mapping: ActionMapping }>,
    ): Record<string, boolean> {
        const results: Record<string, boolean> = {};
        const resourceTypes: ResourceType[] = [
            'playlist',
            'collection',
            'query',
            'upload',
            'tag',
            'tutorial',
            'userRecord',
        ];

        // Extract results for each resource type
        resourceTypes.forEach((resourceType) => {
            const typeResults = this._extractResultsForResourceType(response, items, resourceType);
            Object.assign(results, typeResults);
        });

        // Set false for any items that didn't get results
        items.forEach(({ action }) => {
            if (!(action in results)) {
                results[action] = false;
            }
        });

        return results;
    }

    /**
     * Extract status from frontend action name (draft or live)
     */
    private _extractStatusFromAction(frontendAction: string): 'draft' | 'live' {
        return frontendAction.startsWith('live-') ? 'live' : 'draft';
    }

    /**
     * Build placeholder resource for create actions
     * @param resourceType Type of resource ('playlist' | 'collection' | 'tutorial')
     * @param status Status ('draft' | 'live')
     * @param sapCode Optional SAP code for specific product.
     * @param fiCode Optional FI code for specific product.
     * @returns Placeholder resource object
     */
    private _buildPlaceholderResource(
        resourceType: 'playlist' | 'collection' | 'tutorial',
        status: 'draft' | 'live',
        sapCode?: string,
        fiCode?: string,
        locale: string = 'en_US',
    ): Record<string, unknown> {
        if (status !== 'draft' && status !== 'live') {
            throw new Error(`Status is required and must be 'draft' or 'live'. Received: ${String(status)}`);
        }
        const baseResource = {
            aem_id: `${resourceType}/placeholder-id-${status}`,
            locale,
            status,
        };

        if (resourceType === 'playlist') {
            return {
                ...baseResource,
                applications: {
                    sap_code: sapCode == null ? [] : [sapCode],
                    fi_code: fiCode == null ? [] : [fiCode],
                },
            };
        }

        if (resourceType === 'collection') {
            return {
                ...baseResource,
                applications: {
                    sap_code: sapCode == null ? [] : [sapCode],
                    fi_code: fiCode == null ? [] : [fiCode],
                },
            };
        }

        // Tutorial resources use metadata.application with strings (not arrays)
        return {
            ...baseResource,
            metadata: {
                application: {
                    sap_code: sapCode,
                    fi_code: fiCode,
                },
            },
        };
    }

    /**
     * Build placeholder resources for all primary products (for collection, playlist, and tutorial creation checks)
     * @param resourceType Type of resource ('collection' | 'playlist' | 'tutorial')
     * @param status Status ('draft' | 'live')
     * @param locale Locale for the placeholder resources (default 'en_US')
     * @returns Array of placeholder resource objects, one for each primary product
     */
    private _buildPlaceholderResourcesForAllPrimaryProducts(
        resourceType: 'collection' | 'playlist' | 'tutorial',
        status: 'draft' | 'live',
        locale: string = 'en_US',
    ): Array<Record<string, unknown>> {
        const placeholders: Array<Record<string, unknown>> = [];

        PL_CREATION_PRIMARY_PRODUCT.forEach((product: { id: string; name: string }) => {
            const application = getApplicationFromSapCode(product.id);
            if (application && application.fiCode) {
                const placeholder = this._buildPlaceholderResource(
                    resourceType,
                    status,
                    product.id,
                    application.fiCode,
                    locale,
                );
                // Make each placeholder unique by including the product ID in the aem_id
                placeholder.aem_id = `${resourceType}/placeholder-id-${product.id.toLowerCase()}-${status}`;
                placeholders.push(placeholder);
            }
        });

        return placeholders;
    }
}

// Export singleton instance (following pattern of other services like ModelsService)
const authorizationService = new AuthorizationService();
export default authorizationService;
